import { RefreshCw } from "lucide-react";
import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Match, Schema } from "effect";
import { ApiError } from "#api.ts";
import { useAuth } from "#auth.tsx";
import { runEffectQuery } from "#effect-query.ts";
import { useOrders } from "#orders-context.tsx";
import { formatTime } from "#orders.ts";
import { useLanguage } from "#i18n.tsx";

const analyticsSchema = Schema.Struct({
  data: Schema.Array(
    Schema.Struct({
      hour: Schema.String,
      totalOrders: Schema.Number,
      totalRevenue: Schema.Number,
      averageOrderValue: Schema.Number,
    }),
  ),
  summary: Schema.Struct({
    totalOrders: Schema.Number,
    totalRevenue: Schema.Number,
    averageOrderValue: Schema.Number,
    peakHour: Schema.NullOr(Schema.String),
  }),
});
const money = (value: number) =>
  new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(
    value / 100,
  );

/** Restored 24-hour operational metrics with hourly charts and an accessible data table. */
export function DashboardPage() {
  const { api } = useAuth();
  const { t } = useLanguage();
  const { orders } = useOrders();
  const queryClient = useQueryClient();
  const ordersRevision = orders.reduce(
    (latest, order) => (order.updatedAt > latest ? order.updatedAt : latest),
    "",
  );
  const observedOrdersRevision = useRef(ordersRevision);
  const analytics = useQuery<typeof analyticsSchema.Type, ApiError>({
    queryKey: ["orders", "analytics", "24h"],
    retry: false,
    queryFn: ({ signal }) =>
      runEffectQuery(
        api.requestEffect({
          path: "/orders/analytics/24h",
          schema: analyticsSchema,
          signal,
        }),
        signal,
      ),
  });
  useEffect(() => {
    if (ordersRevision === observedOrdersRevision.current) return;

    observedOrdersRevision.current = ordersRevision;
    const timer = setTimeout(
      () =>
        void queryClient.invalidateQueries({
          queryKey: ["orders", "analytics", "24h"],
        }),
      300,
    );

    return () => clearTimeout(timer);
  }, [ordersRevision, queryClient]);
  return (
    <div>
      <header className="page-header">
        <div>
          <p className="eyebrow">{t("dashboard.eyebrow")}</p>
          <h1>{t("dashboard.title")}</h1>
          <p className="muted">{t("dashboard.description")}</p>
        </div>
        <button
          className="secondary"
          disabled={analytics.isFetching}
          onClick={() => void analytics.refetch()}
        >
          <RefreshCw />
          {t("dashboard.refresh")}
        </button>
      </header>
      {Match.value(analytics).pipe(
        Match.when({ status: "pending" }, () => (
          <p role="status">{t("dashboard.loading")}</p>
        )),
        Match.when({ status: "error" }, ({ error }) =>
          Match.valueTags(error, {
            ApiError: (failure) => (
              <p className="alert" role="alert">
                {failure.message}
              </p>
            ),
          }),
        ),
        Match.when({ status: "success" }, ({ data }) => (
          <>
          <div className="metrics">
            {[
              [t("dashboard.totalOrders"), data.summary.totalOrders],
              [t("dashboard.revenue"), money(data.summary.totalRevenue)],
              [t("dashboard.averageOrder"), money(data.summary.averageOrderValue)],
              [
                t("dashboard.peakHour"),
                data.summary.peakHour ? formatTime(data.summary.peakHour) : "—",
              ],
            ].map(([label, value]) => (
              <section className="settings-card metric" key={label}>
                <h2>{label}</h2>
                <strong>{value}</strong>
              </section>
            ))}
          </div>
          <div className="analytics-charts">
            {(
              [
                { key: "totalOrders", title: t("dashboard.ordersByHour") },
                { key: "totalRevenue", title: t("dashboard.revenueByHour") },
                { key: "averageOrderValue", title: t("dashboard.averageByHour") },
              ] as const
            ).map(({ key, title }) => (
              <section key={key} className="settings-card">
                <h2>{title}</h2>
                <div
                  className="bar-chart"
                  role="img"
                  aria-label={`${title}; exact values in the hourly breakdown below`}
                >
                  {data.data.map((point) => (
                    <div className="bar-column" key={point.hour}>
                      <div
                        className="chart-bar"
                        style={{
                          height: `${Math.max(1, (point[key] / Math.max(1, ...data.data.map((item) => item[key]))) * 100)}%`,
                        }}
                        title={`${formatTime(point.hour)}: ${key === "totalOrders" ? point[key] : money(point[key])}`}
                      />
                    </div>
                  ))}
                </div>
                <div className="chart-axis">
                  <span>{data.data[0] && formatTime(data.data[0].hour)}</span>
                  <span>
                    {data.data.at(-1) &&
                      formatTime(data.data.at(-1)?.hour ?? "")}
                  </span>
                </div>
              </section>
            ))}
          </div>
          <details className="settings-card">
            <summary>{t("dashboard.hourly")}</summary>
            <div className="table-scroll">
              <table className="orders-table">
                <thead>
                  <tr>
                    <th>{t("dashboard.hour")}</th>
                    <th>{t("dashboard.orders")}</th>
                    <th>{t("dashboard.revenue")}</th>
                    <th>{t("dashboard.average")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((point) => (
                    <tr key={point.hour}>
                      <td>{formatTime(point.hour, true)}</td>
                      <td>{point.totalOrders}</td>
                      <td>{money(point.totalRevenue)}</td>
                      <td>{money(point.averageOrderValue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
          </>
        )),
        Match.exhaustive,
      )}
    </div>
  );
}
